import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatCurrency, formatNumber } from './calculations';

/**
 * Generate PDF version of the Executive Report
 */
export const generateExecutiveReportPDF = (results, inputs, products, userEmail) => {
  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  const isHospital = inputs.facilityType === 'hospital';
  const enabledProducts = products.filter(p => p.enabled !== false);
  
  let yPos = margin;
  
  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace = 20) => {
    if (yPos + requiredSpace > pageHeight - margin) {
      pdf.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };
  
  // Helper function to add section header
  const addSectionHeader = (title, color = [37, 99, 235]) => {
    checkPageBreak(30);
    pdf.setFillColor(...color);
    pdf.rect(margin, yPos, 4, 8, 'F');
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(title, margin + 8, yPos + 6);
    yPos += 15;
  };
  
  // Helper function to add body text
  const addBodyText = (text, indent = 0) => {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    const lines = pdf.splitTextToSize(text, contentWidth - indent);
    
    lines.forEach(line => {
      checkPageBreak(10);
      pdf.text(line, margin + indent, yPos);
      yPos += 6;
    });
    yPos += 2;
  };
  
  // ===== HEADER =====
  pdf.setFillColor(37, 99, 235);
  pdf.rect(0, 0, pageWidth, 50, 'F');
  
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('EHS Staffing Justification Report', margin, 20);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Risk-Based FTE Requirements Analysis', margin, 30);
  
  pdf.setFontSize(9);
  pdf.text(`Prepared: ${today}`, margin, 40);
  pdf.text(`Prepared by: ${userEmail}`, margin, 45);
  pdf.text(`Facility Type: ${isHospital ? 'Academic Hospital' : 'General Industry'}`, pageWidth - margin - 60, 45);
  
  yPos = 60;
  
  // ===== EXECUTIVE SUMMARY =====
  addSectionHeader('Executive Summary', [37, 99, 235]);
  
  // Key metrics box
  pdf.setFillColor(239, 246, 255);
  pdf.rect(margin, yPos, contentWidth, 30, 'F');
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 100, 100);
  pdf.text('Required FTE', margin + 10, yPos + 10);
  pdf.text('Annual Investment', pageWidth / 2 + 10, yPos + 10);
  
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(37, 99, 235);
  pdf.text(results.totalFTEs.toFixed(2), margin + 10, yPos + 22);
  pdf.setTextColor(34, 197, 94);
  pdf.text(formatCurrency(results.totalCost), pageWidth / 2 + 10, yPos + 22);
  
  yPos += 40;
  
  addBodyText(
    `Based on a comprehensive risk-based workload analysis, this organization requires ${results.totalFTEs.toFixed(2)} Full-Time Equivalents (FTE) for Environmental Health & Safety (EHS) operations. This staffing level is justified through a data-driven methodology that accounts for workload complexity, regulatory requirements, organizational risk profile, and industry benchmarks.`
  );
  
  addBodyText(
    `The recommended staffing represents a total annual investment of ${formatCurrency(results.totalCost)} (including salary and benefits), which is essential to maintain regulatory compliance, protect employee safety, minimize environmental liability, and support operational continuity.`
  );
  
  if (isHospital && results.benchmarkFTE > 0) {
    addBodyText(
      `Industry Validation: The calculated FTE requirement of ${results.totalFTEs.toFixed(2)} is ${results.benchmarkStatus === 'good' ? 'aligned with' : results.benchmarkStatus === 'warning' ? 'moderately above' : 'significantly different from'} the industry benchmark of ${results.benchmarkFTE.toFixed(2)} FTE (variance: ${(results.benchmarkVariance * 100).toFixed(1)}%).`
    );
  }
  
  // ===== METHODOLOGY =====
  checkPageBreak(40);
  addSectionHeader('Calculation Methodology', [34, 197, 94]);
  
  addBodyText('This analysis employs a risk-based activity workload model that calculates FTE requirements based on:');
  
  yPos += 3;
  const methodologyItems = [
    'Net Available Hours: Annual work hours adjusted for leave and administrative time',
    'Activity-Based Workload: Individual EHS activities weighted by complexity and risk',
    'Core Program Management: Base hours for program oversight and strategic planning',
    'Logistical Support: Administrative functions, training coordination, and communication'
  ];
  
  if (isHospital) {
    methodologyItems.push('Fire Safety & Life Safety Code Compliance: Hospital-specific requirements');
    methodologyItems.push('Environmental Compliance: Medical waste, wastewater, and air quality management');
  }
  
  methodologyItems.push('Population Scaling: Workload adjusted based on employee population');
  
  methodologyItems.forEach((item, idx) => {
    checkPageBreak(10);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    const lines = pdf.splitTextToSize(`${idx + 1}. ${item}`, contentWidth - 10);
    lines.forEach(line => {
      pdf.text(line, margin + 5, yPos);
      yPos += 6;
    });
  });
  
  yPos += 5;
  
  // Formula box
  checkPageBreak(35);
  pdf.setFillColor(245, 245, 245);
  pdf.rect(margin, yPos, contentWidth, 30, 'F');
  pdf.setDrawColor(200, 200, 200);
  pdf.rect(margin, yPos, contentWidth, 30, 'S');
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('Formula Summary:', margin + 5, yPos + 8);
  
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(9);
  pdf.text('FTE = Total Workload Hours / Productive Hours per FTE', margin + 5, yPos + 16);
  pdf.text('Productive Hours = (Annual Hours - Leave) × (1 - Admin %)', margin + 5, yPos + 22);
  
  yPos += 40;
  
  // ===== WORKLOAD ANALYSIS =====
  checkPageBreak(40);
  addSectionHeader('Workload Analysis', [168, 85, 247]);
  
  // 1. Productive Hours
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('1. Net Productive Hours per FTE', margin, yPos);
  yPos += 8;
  
  const productiveHoursData = [
    ['Annual Work Hours', formatNumber(inputs.totalHours)],
    ['Less: Leave Hours', `-${formatNumber(inputs.leaveHours)}`],
    [`Less: Admin Time (${inputs.adminPercent}%)`, `-${formatNumber(results.adminHours)}`],
    ['Net Productive Hours per FTE', `${formatNumber(results.productiveHoursFTE)} hours/year`]
  ];
  
  pdf.autoTable({
    startY: yPos,
    head: [],
    body: productiveHoursData,
    margin: { left: margin, right: margin },
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.6 },
      1: { cellWidth: contentWidth * 0.4, halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: (data) => {
      if (data.row.index === productiveHoursData.length - 1) {
        data.cell.styles.fillColor = [220, 252, 231];
        data.cell.styles.textColor = [22, 163, 74];
      }
    }
  });
  
  yPos = pdf.lastAutoTable.finalY + 10;
  
  // 2. Total Workload
  checkPageBreak(40);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('2. Total Annual Workload Requirements', margin, yPos);
  yPos += 8;
  
  const workloadData = [
    ['Core Program Hours', `${formatNumber(inputs.coreHours)} hrs`],
    ['Activity-Based Hours (scaled)', `${formatNumber(results.scaledActivityHours)} hrs`],
    ['Logistical Support Hours', `${formatNumber(inputs.logisticsHours)} hrs`]
  ];
  
  if (isHospital && results.fireSafetyHours > 0) {
    workloadData.push(['Fire Safety Hours', `${formatNumber(results.fireSafetyHours)} hrs`]);
  }
  if (isHospital && results.environmentalHours > 0) {
    workloadData.push(['Environmental Compliance Hours', `${formatNumber(results.environmentalHours)} hrs`]);
  }
  
  workloadData.push(['Total Annual Workload', `${formatNumber(results.totalWorkloadHours)} hours/year`]);
  
  pdf.autoTable({
    startY: yPos,
    head: [],
    body: workloadData,
    margin: { left: margin, right: margin },
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.6 },
      1: { cellWidth: contentWidth * 0.4, halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: (data) => {
      if (data.row.index === workloadData.length - 1) {
        data.cell.styles.fillColor = [219, 234, 254];
        data.cell.styles.textColor = [37, 99, 235];
      }
    }
  });
  
  yPos = pdf.lastAutoTable.finalY + 10;
  
  // 3. Activity Detail
  checkPageBreak(40);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('3. Activity-Based Workload Detail', margin, yPos);
  yPos += 8;
  
  const activityTableData = enabledProducts.map(product => {
    const weighted = product.baseHours * (1 + (product.comp / 100) + (product.risk / 100) + (product.env / 100));
    return [
      product.name.substring(0, 40),
      product.baseHours.toString(),
      `${product.risk}%`,
      Math.round(weighted).toString()
    ];
  });
  
  pdf.autoTable({
    startY: yPos,
    head: [['Activity', 'Base Hrs', 'Risk %', 'Weighted Hrs']],
    body: activityTableData,
    margin: { left: margin, right: margin },
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.5 },
      1: { cellWidth: contentWidth * 0.17, halign: 'right' },
      2: { cellWidth: contentWidth * 0.17, halign: 'right' },
      3: { cellWidth: contentWidth * 0.16, halign: 'right', fontStyle: 'bold' }
    }
  });
  
  yPos = pdf.lastAutoTable.finalY + 5;
  
  // ===== BUSINESS JUSTIFICATION =====
  pdf.addPage();
  yPos = margin;
  addSectionHeader('Business Justification', [249, 115, 22]);
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Regulatory Compliance & Risk Management', margin, yPos);
  yPos += 8;
  
  addBodyText(
    `The calculated FTE requirement ensures adequate staffing to maintain compliance with federal, state, and local regulations including OSHA standards, EPA requirements${isHospital ? ', Joint Commission standards, CMS conditions of participation, and NFPA Life Safety Code' : ''}. Insufficient EHS staffing creates significant organizational risk including regulatory penalties, legal liability, operational disruptions, and reputational damage.`
  );
  
  yPos += 5;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Cost-Benefit Analysis', margin, yPos);
  yPos += 8;
  
  addBodyText(
    `The annual investment of ${formatCurrency(results.totalCost)} for ${results.totalFTEs.toFixed(2)} FTE provides substantial value through risk mitigation. Average OSHA fines range from $7,000-$15,000 per violation, workers' compensation claims average $40,000-$100,000, and major incident downtime can exceed $500,000.`
  );
  
  // ===== RECOMMENDATION =====
  checkPageBreak(40);
  addSectionHeader('Recommendation', [34, 197, 94]);
  
  pdf.setFillColor(220, 252, 231);
  pdf.rect(margin, yPos, contentWidth, 25, 'F');
  pdf.setDrawColor(34, 197, 94);
  pdf.setLineWidth(0.5);
  pdf.rect(margin, yPos, contentWidth, 25, 'S');
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 60);
  const recText = pdf.splitTextToSize(
    `Based on the comprehensive risk-based workload analysis, it is recommended that the organization authorize ${results.totalFTEs.toFixed(2)} Full-Time Equivalent (FTE) positions for EHS operations, representing an annual investment of ${formatCurrency(results.totalCost)}. This staffing level is essential to maintain regulatory compliance, protect organizational assets, and support sustainable operational performance.`,
    contentWidth - 10
  );
  
  let recYPos = yPos + 8;
  recText.forEach(line => {
    pdf.text(line, margin + 5, recYPos);
    recYPos += 5;
  });
  
  yPos += 35;
  
  // ===== DISCLAIMER =====
  checkPageBreak(60);
  pdf.setFillColor(254, 249, 195);
  pdf.rect(margin, yPos, contentWidth, 3, 'F');
  yPos += 8;
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('⚠ DISCLAIMER', margin, yPos);
  yPos += 10;
  
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);
  
  const disclaimerText = [
    'This report is for informational and planning purposes only. The FTE calculations are based on the input data and assumptions provided at the time of analysis. Actual staffing requirements may vary based on organizational changes, regulatory updates, and other factors.',
    'No Guarantee of Regulatory Compliance: This analysis does not constitute legal advice and does not guarantee compliance with applicable regulations. Organizations should consult with qualified legal counsel and EHS professionals.',
    'Professional Judgment Required: Staffing decisions should be made in consultation with qualified EHS professionals who understand the organization\'s specific operations, risk profile, and regulatory environment.',
    'By using this report, you acknowledge that staffing decisions are the sole responsibility of the organization and that the report authors and software developers assume no liability for outcomes resulting from this analysis.'
  ];
  
  disclaimerText.forEach(para => {
    const lines = pdf.splitTextToSize(para, contentWidth - 10);
    lines.forEach(line => {
      checkPageBreak(8);
      pdf.text(line, margin + 5, yPos);
      yPos += 4;
    });
    yPos += 3;
  });
  
  // ===== FOOTER =====
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      `EHS Risk-Based Staffing Modeler Pro | ${today}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }
  
  // Save PDF
  const filename = `EHS_Staffing_Report_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(filename);
  
  return filename;
};
